import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { LogoMark } from "./Logo";
import { SearchOverlay } from "./SearchOverlay";
import { useSite } from "../context/SiteContext";

export const Navbar = () => {
  const { site } = useSite();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const knock = useRef({ count: 0, timer: null });
  const sections = site?.sections || [];
  const title = site?.settings?.site_title || "The Obsidian Vault";

  // The secret knock — three quick raps on the monogram open the Sanctum door
  const onKnock = () => {
    knock.current.count += 1;
    clearTimeout(knock.current.timer);
    if (knock.current.count >= 3) {
      knock.current.count = 0;
      navigate("/admin");
      return;
    }
    knock.current.timer = setTimeout(() => { knock.current.count = 0; }, 700);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-[#232530] bg-[rgba(7,7,9,0.92)] backdrop-blur-xl" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onKnock} data-testid="logo-knock" aria-label="The vault monogram"
            className="cursor-default select-none">
            <LogoMark size={42} />
          </button>
          <Link to="/" className="group" data-testid="nav-home-link">
            <span className="font-display text-sm tracking-[0.2em] uppercase text-[#ede8e1] group-hover:text-[#c9c4bc] transition-colors">
              {title}
            </span>
          </Link>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          {sections.map((s) => (
            <Link key={s.id} to={`/s/${s.slug}`}
              data-testid={`nav-link-${s.slug}`}
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#a8a29e] hover:text-[#ede8e1] transition-colors">
              {s.slug === "portfolio" ? "Projects" : s.title.split("&")[0].trim()}
            </Link>
          ))}
          <button onClick={() => setSearchOpen(true)} data-testid="nav-search-button"
            className="text-[#a8a29e] hover:text-[#ede8e1] transition-colors" aria-label="Search the vault">
            <Search size={17} />
          </button>
        </nav>
        <div className="md:hidden flex items-center gap-4">
          <button onClick={() => setSearchOpen(true)} className="text-[#ede8e1]" data-testid="nav-search-button-mobile" aria-label="Search the vault">
            <Search size={20} />
          </button>
          <button className="text-[#ede8e1]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="md:hidden border-t border-[#232530] bg-[rgba(7,7,9,0.97)] px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          {sections.map((s) => (
            <Link key={s.id} to={`/s/${s.slug}`} onClick={() => setOpen(false)}
              className="font-mono text-xs tracking-[0.25em] uppercase text-[#a8a29e]">
              {s.title}
            </Link>
          ))}
        </nav>
      )}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
